import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Leaf, Home, Building2, BookOpen, FlaskConical, Boxes, Compass, Target, Network, Ruler } from "lucide-react";
import { CONTENT_TYPES } from "../data/ecosystem";
import WaitlistModal from "../components/WaitlistModal";
import Seo from "../components/Seo";

const PRINCIPLES = [
  { icon: Target, title: "Prioritize First", desc: "Know which changes have the greatest impact, and which can safely wait." },
  { icon: FlaskConical, title: "Evidence-Based", desc: "Recommendations backed by current research, not trends or fear-based messaging." },
  { icon: Network, title: "Systems Thinking", desc: "Optimize your home as a system instead of chasing individual products." },
  { icon: Ruler, title: "Practical Frameworks", desc: "Simple, reusable decision tools designed for busy people and families." },
];

const JOURNEY = [
  { key: "learn", step: "Learn", name: "Greenpapers", icon: BookOpen, color: "#00E599", to: "/greenpapers", cta: "Explore Greenpapers",
    desc: "Understand cumulative exposure, evaluate tradeoffs, and learn how to think about environmental health through structured engineering analyses." },
  { key: "try", step: "Try", name: "Sample Library", icon: FlaskConical, color: "#43C6F0", to: null, cta: "Join the Waitlist", status: "Coming Soon",
    desc: "Discover and evaluate carefully selected lower-tox products before replacing what you currently use." },
  { key: "implement", step: "Implement", name: "Products", icon: Boxes, color: "#FFB259", to: "/products", cta: "Explore Products",
    desc: "Digital Blueprints, Frameworks, Toolkits, and carefully curated products that help translate knowledge into practical implementation." },
  { key: "optimize", step: "Optimize", name: "Services", icon: Compass, color: "#B98CFF", to: "/services", cta: "Explore Services",
    desc: "Work directly with My Eco Engineer to design healthier homes, more resilient households, and higher-performing organizations using structured engineering methodologies." },
];

const PRACTICE_AREAS = [
  {
    key: "environmental", icon: Leaf, color: "#00E599", name: "Environmental Systems", tag: "Our launch focus",
    desc: "Engineering healthier homes through structured evaluation of cumulative environmental exposure.",
    includes: ["Greenpapers", "Home Blueprints", "Environmental Frameworks", "Decision Toolkits"],
  },
  {
    key: "household", icon: Home, color: "#FFB259", name: "Household Systems", tag: "In development",
    desc: "Engineering more resilient households through better decision-making and cognitive load management.",
    includes: ["Cognitive Load & Allocation Framework", "Decision Systems Toolkit", "Household Operating Systems (future)"],
  },
  {
    key: "organizational", icon: Building2, color: "#B98CFF", name: "Organizational Systems", tag: "Coming soon",
    desc: "Engineering higher-performing organizations through structured decision systems, workflow optimization, cognitive load management, and AI-assisted operations.",
    includes: ["Organizational Cognitive Load Framework", "Decision Architecture Toolkit", "AI Workflow Blueprint"],
  },
];

const StatusBadge = ({ color, children }) => (
  <span
    className="inline-flex items-center gap-1.5 px-3 py-1 font-mono text-[10px] uppercase tracking-widest border rounded-full whitespace-nowrap"
    style={{ color, borderColor: `${color}66`, backgroundColor: `${color}12` }}
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
    {children}
  </span>
);

export default function EcosystemPage() {
  return (
    <div data-testid="ecosystem-page">
      <Seo title="The Ecosystem" path="/ecosystem"
        description="How My Eco Engineer fits together: Greenpapers, Frameworks, Blueprints, Toolkits, and Systems, one integrated decision system to learn, decide, implement, and improve." />

      {/* HERO */}
      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <p className="mono-label">The Ecosystem</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.06]">
            Everything Works Together.<br /><span className="text-primary">So Should Your Approach.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            My Eco Engineer is built as an integrated decision system. Rather than offering isolated articles or
            product recommendations, we provide connected resources that help you understand environmental health,
            organize decisions, implement meaningful changes, and build durable systems over time.
          </p>

          {/* Customer journey stepper: Learn -> Try -> Implement -> Optimize */}
          <div className="mt-12" data-testid="ecosystem-progression">
            <p className="mono-label mb-4">The customer journey</p>
            <div className="flex flex-wrap items-center gap-y-3">
              {JOURNEY.map((t, i) => (
                <div key={t.key} className="flex items-center">
                  <div className="flex items-center gap-2.5 px-3.5 py-2.5 border bg-base" style={{ borderColor: `${t.color}55` }}>
                    <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: t.color }}>{t.step}</span>
                    <span className="text-sm font-medium text-foreground/85">{t.name}</span>
                    {t.status && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border rounded-full" style={{ color: t.color, borderColor: `${t.color}66` }}>{t.status}</span>}
                  </div>
                  {i < JOURNEY.length - 1 && (
                    <ArrowRight className="h-4 w-4 mx-2 text-foreground/30 shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE CUSTOMER JOURNEY — primary organizing framework */}
      <section className="border-b border-border" data-testid="ecosystem-journey">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="mono-label">Learn → Try → Implement → Optimize</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight max-w-3xl">
            One journey, four connected stages.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-foreground/65 leading-relaxed">
            Every part of My Eco Engineer maps to a stage in your journey, from building understanding, to trying
            alternatives, to implementing durable changes, to optimizing with expert help.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {JOURNEY.map((t, i) => (
              <div key={t.key} data-testid={`journey-${t.key}`} className="group relative flex flex-col bg-base p-8 overflow-hidden">
                <span className="absolute top-0 left-0 h-[3px] w-full" style={{ background: t.color }} />
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: `${t.color}66`, color: t.color }}>
                    <t.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-[10px]" style={{ color: t.color }}>{String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-widest" style={{ color: t.color }}>{t.step}</p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-xl font-medium">{t.name}</h3>
                  {t.status && (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border rounded-full" style={{ color: t.color, borderColor: `${t.color}66`, backgroundColor: `${t.color}12` }}>
                      <span className="h-1 w-1 rounded-full" style={{ background: t.color }} />{t.status}
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-foreground/65 leading-relaxed flex-1">{t.desc}</p>
                {t.to ? (
                  <Link to={t.to} data-testid={`journey-cta-${t.key}`}
                    className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest hover:gap-3 transition-all" style={{ color: t.color }}>
                    {t.cta} <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                  </Link>
                ) : (
                  <WaitlistModal
                    slug="sample-library"
                    name="Sample Library"
                    triggerTestid={`journey-cta-${t.key}`}
                    triggerClassName="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer hover:gap-3 transition-all"
                  >
                    <span style={{ color: t.color }} className="inline-flex items-center gap-2">{t.cta} <ArrowRight className="h-3.5 w-3.5 shrink-0" /></span>
                  </WaitlistModal>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDING PRINCIPLES OF THE METHODOLOGY */}
      <section className="border-b border-border bg-surface/30" data-testid="ecosystem-principles">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="mono-label">The methodology · four guiding principles</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight max-w-2xl">
            How every part of the ecosystem is built.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} data-testid={`principle-${i}`} className="relative flex flex-col bg-base p-8 overflow-hidden">
                <span className="absolute top-0 left-0 h-[3px] w-full bg-primary/60" />
                <span className="flex h-11 w-11 items-center justify-center border border-primary/40 text-primary">
                  <p.icon className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <span className="mt-6 font-mono text-xs text-primary">{`0${i + 1}`}</span>
                <h3 className="mt-2 font-display text-xl font-medium">{p.title}</h3>
                <p className="mt-2 text-sm text-foreground/60 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ONE METHODOLOGY, THREE PRACTICE AREAS */}
      <section className="border-b border-border bg-surface/20" data-testid="practice-areas">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="mono-label">One methodology, three practice areas</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight max-w-3xl">
            The same engineering methodology, applied to different complex systems.
          </h2>
          <p className="mt-4 max-w-2xl text-base text-foreground/65 leading-relaxed">
            Environmental health is where we prove the method, and where we're focused today. Over time, the same
            systems thinking extends to households and organizations.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-border border border-border">
            {PRACTICE_AREAS.map((a) => (
              <div key={a.key} data-testid={`practice-${a.key}`} className="bg-base p-8 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: `${a.color}66`, color: a.color }}>
                    <a.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-full" style={{ color: a.color, borderColor: `${a.color}66`, backgroundColor: `${a.color}12` }}>{a.tag}</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-medium">{a.name}</h3>
                <p className="mt-3 text-sm text-foreground/65 leading-relaxed flex-1">{a.desc}</p>
                <ul className="mt-5 space-y-1.5">
                  {a.includes.map((it) => (
                    <li key={it} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-foreground/55">
                      <span className="h-1 w-1 rounded-full shrink-0" style={{ background: a.color }} />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="space-y-4">
          {CONTENT_TYPES.map((t, i) => (
            <div key={t.key}>
              <div data-testid={`ecosystem-${t.key}`}
                className="group relative bg-base border border-border hover:bg-surface transition-colors overflow-hidden">
                <span className="absolute left-0 top-0 h-full w-1" style={{ background: t.color }} />
                <div className="grid md:grid-cols-12 gap-6 p-8 md:p-10 pl-9 md:pl-12">
                  <div className="md:col-span-4">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl font-bold leading-none" style={{ color: t.color }}>{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: t.color }}>{t.step}</span>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center border" style={{ borderColor: `${t.color}66`, color: t.color }}>
                        <t.icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                      <div>
                        <h2 className="font-display text-2xl md:text-3xl font-medium">{t.name}</h2>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-foreground/45">{t.category}</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-base text-foreground/70 leading-relaxed">{t.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {t.items.map((it) => (
                        <span key={it} className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-border text-foreground/55">{it}</span>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-3 flex md:flex-col md:items-end justify-between gap-3">
                    <StatusBadge color={t.color}>{t.status}</StatusBadge>
                    <Link to={t.to} data-testid={`ecosystem-cta-${t.key}`}
                      className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest hover:gap-3 transition-all text-right" style={{ color: t.color }}>
                      {t.cta} <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  </div>
                </div>
              </div>
              {i < CONTENT_TYPES.length - 1 && (
                <div className="flex justify-center py-2" aria-hidden="true">
                  <ChevronDown className="h-5 w-5 text-foreground/25" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT MAPS TO WHAT WE OFFER */}
      <section className="border-t border-border bg-surface/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="mono-label">How the ecosystem maps to what we offer</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight max-w-3xl">
            One foundation, a family of products, and hands-on help when you want it.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-border border border-border">
            <div data-testid="map-foundation" className="bg-base p-8">
              <p className="font-mono text-[11px] uppercase tracking-widest text-primary">The foundation</p>
              <h3 className="mt-3 font-display text-xl font-medium">Greenpapers</h3>
              <p className="mt-3 text-sm text-foreground/65 leading-relaxed">
                Free, research-backed education. The starting point that makes every product and decision easier to understand.
              </p>
            </div>
            <div data-testid="map-products" className="bg-base p-8">
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#FFB259]">The products</p>
              <h3 className="mt-3 font-display text-xl font-medium">Frameworks · Blueprints · Toolkits · Systems</h3>
              <p className="mt-3 text-sm text-foreground/65 leading-relaxed">
                The paid offerings you can buy, from decision models and implementation guides to practical resources and complete systems.
              </p>
            </div>
            <div data-testid="map-services" className="bg-base p-8">
              <p className="font-mono text-[11px] uppercase tracking-widest text-primary">The services</p>
              <h3 className="mt-3 font-display text-xl font-medium">Done-with-you help</h3>
              <p className="mt-3 text-sm text-foreground/65 leading-relaxed">
                Hands-on guidance to implement a complete System when you want a faster, tailored path.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT FITS TOGETHER — executive summary */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="mono-label">How it fits together</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight">The whole system in five steps.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border">
            {CONTENT_TYPES.map((t, i) => (
              <div key={t.key} data-testid={`fits-${t.key}`} className="bg-base p-6 flex flex-col">
                <span className="font-mono text-2xl font-light" style={{ color: t.color }}>{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-lg font-medium">{t.name}</h3>
                <p className="mt-1 text-sm text-foreground/60 capitalize">{t.fits}.</p>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-lg md:text-xl font-display font-light leading-snug text-foreground/90">
            Not a collection of disconnected articles or products, but one integrated system that helps you{" "}
            <span className="text-primary">learn, decide, implement, and continuously improve</span>.
          </p>
        </div>
      </section>
    </div>
  );
}
